<template>
  <div
    ref="customizationEditorEl"
    class="customization-editor"
    :data-cy="`customization-editor-${customization.key}`"
    :class="{ fullSize, isGlobal }"
  >
    <div class="d-flex align-items-baseline mb-2">
      <div
        ref="contentDescriptionContainerEl"
        class="flex-1"
        data-cy="content-description"
      >
        <div>
          <strong class="content-label">{{
            customization.label.replace(/_/g, " ")
          }}</strong>
          <i
            class="fa-regular text-primary fa-circle-question info-tooltip ml-1"
            :data-tippy-content="customization.description"
          ></i>
        </div>
        <div v-if="isGlobal" class="note">
          <i class="fa fa-globe mr-2"></i>
          <span>This content is shared with other email templates.</span>
        </div>
      </div>
      <button
        class="ml-auto resetButton small"
        :class="{ invisible: !showResetToDefault }"
        data-cy="content-reset-to-default"
        @click="handleResetContent(customization)"
      >
        <i class="far fa-arrow-rotate-left mr-1"></i>
        <span>Reset to default</span>
      </button>
    </div>
    <Select
      v-if="customization.options"
      v-model="customization.value"
      :options="customizationOptions"
      body-render
    />
    <div
      v-else-if="customization.data_type === 'image_url'"
      class="d-flex align-items-center gap-4"
    >
      <img
        :src="customization.value"
        height="55"
        alt="Logo Preview"
        class="h-[55px]"
      />
      <AssetUpload
        :asset="asset"
        :links="{
          file_upload: '/customizations/dark_logo_url/upload_asset'
        }"
        @upload-complete="uploadComplete"
        @upload-canceled="uploadCanceled"
      />
    </div>
    <div v-else ref="rteEditorContainerEl" class="rteEditor">
      <RTEQuill
        v-if="customization.data_type === 'html'"
        :key="`1-${editKeyId}-${customization.key}`"
        v-model="customization.value"
        data-cy="rte"
        :disabled="disabled"
        @ready="handleOnReady"
      />
      <textarea v-else v-model="customization.value" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import RTEQuill from "@/components/RTE/RTEQuill.vue";
import CONSTANTS from "@/Constants";
import AssetUpload from "@/Customizations/v2/AssetUpload.vue";
import { EmailTemplateCustomization } from "./emailTemplate";
const {
  ALLOWED_FILE_EXTENSIONS_LOGO,
  AWS_S3_CDN_ROOT,
  CDN_ROOT,
  CDN_ROOT_TEMPLATE
} = CONSTANTS;

// NOTE: Try not to use `provide` in this element. Currently having reactivity issues when using provide / inject from
// a parent component to an iframe instance.
const props = defineProps({
  hideGlobalMessage: {
    type: Boolean,
    default: false
  },
  fullSize: {
    type: Boolean,
    default: false
  },
  modelValue: {
    type: Object as () => EmailTemplateCustomization,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:modelValue", "reset-to-default", "ready"]);
const rteEditorContainerEl = ref<any>(null);
const contentDescriptionContainerEl = ref<any>(null);
const editKeyId = ref(0);
const customizationEditorEl = ref<any>();

const customization = computed({
  get() {
    return props.modelValue;
  },
  set(val) {
    emit("update:modelValue", val);
  }
});

const asset = ref({
  name: customization.value.description,
  extensions: ALLOWED_FILE_EXTENSIONS_LOGO
});

const customizationOptions = computed(() => {
  if (!customization.value.options) return "";
  return customization.value.options
    .split(",")
    .map((opt) => ({ label: opt, value: opt }));
});

const isGlobal = computed(() => {
  if (props.hideGlobalMessage) return false;
  return props.modelValue.category !== "Emails";
});

const showResetToDefault = computed(() => {
  if (!props.modelValue) return false;
  return !props.modelValue.isDefaultValue;
});

onMounted(() => {
  window.tippy(customizationEditorEl.value!.querySelectorAll(".info-tooltip"), {
    appendTo: "parent"
  });
});

function handleResetContent(customization: EmailTemplateCustomization) {
  emit("reset-to-default", customization);
  editKeyId.value = editKeyId.value + 1;
}

function handleOnReady() {
  if (props.fullSize) {
    rteEditorContainerEl.value.style.height = `calc(100vh - ${
      contentDescriptionContainerEl.value.getBoundingClientRect().height + 40
    }px)`;
  }
  setTimeout(() => {
    emit("ready");
  }, 10);
}

function uploadCanceled() {
  customization.value.value = customization.value.default;
}

function uploadComplete(s3Key) {
  if (s3Key.startsWith("https://") || s3Key.startsWith("http://")) {
    customization.value.value = s3Key;
    return;
  }
  const env = s3Key.substring(0, s3Key.indexOf("/"));
  if (env === "development") {
    customization.value.value = AWS_S3_CDN_ROOT + s3Key;
  } else if (env === "production") {
    customization.value.value = CDN_ROOT + s3Key.substring(env.length);
  } else {
    customization.value.value =
      CDN_ROOT_TEMPLATE.replace(":env", env) + s3Key.substring(env.length);
  }
}
</script>

<style lang="scss" scoped>
.customization-editor {
  .content-label {
    text-transform: capitalize;
  }
  .resetButton {
    background: none;
    border: none;
    color: var(--primary-color);
    outline: none;
    padding: 7.5px 15px;
    cursor: pointer;
    &:hover {
      background: var(--neutral-gray);
    }
  }
  .rteEditor {
    height: 150px;
    min-height: 150px;
    resize: vertical;
    overflow: hidden;
    textarea {
      outline: none;
      padding: 15px;
      width: 100%;
      resize: none;
      height: 100%;
      font-size: 13px;
      border: 1px solid #c7cfd3;
    }
  }
  .note {
    font-size: 0.65rem;
    color: var(--organic-green);
  }
  &.isGlobal {
    .rteEditor {
      :deep(textarea) {
        border: 1px solid var(--organic-green);
      }
      :deep(.ql-toolbar) {
        border: 1px solid var(--organic-green);
      }
      :deep(.rte__editor) {
        border: 1px solid var(--organic-green);
      }
    }
  }
  &.fullSize {
    .rteEditor {
      resize: none;
      overflow: hidden;
      min-height: 200px;
    }
  }
  &.disabled {
    .resetButton {
      opacity: 0;
    }
  }
}
</style>
